import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft, Plus, Pencil, Trash2, Star, ImageOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api, { resolveMedia } from '../lib/api';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger
} from '../components/ui/alert-dialog';

const statusStyles = {
  published: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  draft: 'bg-amber-500/10 text-amber-300 border-amber-500/30'
};

const AdminCaseStudiesPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/case-studies/admin');
      setItems(data);
    } catch {
      toast({ title: 'Failed to load case studies' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const remove = async (id) => {
    try {
      await api.delete(`/case-studies/${id}`);
      toast({ title: 'Deleted' });
      load();
    } catch {
      toast({ title: 'Delete failed' });
    }
  };

  const filtered = filter === 'all' ? items : items.filter((i) => i.status === filter);
  const counts = items.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen bg-[#05080f]">
      <header className="border-b border-white/5 bg-[#05080f]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <div className="h-4 w-px bg-white/10"></div>
            <h1 className="text-white font-display text-lg">Case Studies</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-white text-sm">{user?.username}</div>
              <div className="text-slate-500 text-xs">{user?.email}</div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10">
              <LogOut size={14} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">Case studies</h2>
            <p className="text-slate-400 mt-1 text-sm">Publish industry-specific outcomes visitors can browse on the home page.</p>
          </div>
          <Link to="/admin/case-studies/new">
            <Button className="btn-gradient rounded-full h-10 px-5 text-white border-0" data-testid="new-case-study-btn">
              <Plus size={16} className="mr-2" /> New case study
            </Button>
          </Link>
        </div>

        <div className="flex gap-2 flex-wrap mb-6">
          {['all', 'published', 'draft'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors capitalize ${
                filter === f
                  ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                  : 'bg-white/[0.03] text-slate-300 border-white/10 hover:border-amber-500/30'
              }`}
            >
              {f} {f !== 'all' && counts[f] ? `(${counts[f]})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-slate-400 py-16 text-center">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="card-dark rounded-3xl p-12 text-center">
            <p className="text-slate-300">No case studies yet.</p>
            <Link to="/admin/case-studies/new">
              <Button className="btn-gradient rounded-full h-10 px-5 text-white border-0 mt-5">
                <Plus size={16} className="mr-2" /> Create your first
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((c) => (
              <div key={c.id} className="card-dark rounded-2xl p-5 flex flex-col md:flex-row gap-5 items-start">
                <div className="h-24 w-40 shrink-0 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                  {c.thumbnail ? (
                    <img src={resolveMedia(c.thumbnail)} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <ImageOff size={18} className="text-slate-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-white font-semibold">{c.title}</h3>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${statusStyles[c.status] || statusStyles.draft}`}>{c.status}</span>
                    {c.featured && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30 inline-flex items-center gap-1">
                        <Star size={10} /> Featured
                      </span>
                    )}
                  </div>
                  <div className="text-amber-400 text-sm mt-0.5">{c.industry}{c.client ? ` · ${c.client}` : ''}</div>
                  <p className="text-slate-400 text-sm mt-2 line-clamp-2">{c.outcome || c.challenge}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(c.metrics || []).slice(0, 4).map((m) => (
                      <span key={m} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-200 border border-amber-500/25">{m}</span>
                    ))}
                    {(c.tags || []).slice(0, 4).map((t) => (
                      <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300 border border-white/10">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 md:self-center">
                  <Link to={`/admin/case-studies/${c.id}/edit`}>
                    <Button size="sm" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10">
                      <Pencil size={14} />
                    </Button>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20">
                        <Trash2 size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#0b1220] border-white/10 text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this case study?</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-500/80 hover:bg-red-500 text-white">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminCaseStudiesPage;
